import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/error';
import { toast } from '@/lib/toast';
import { resetPasswordRequest } from '@/services/users';
import type { ResetPasswordResponse } from '@/types/users';

/**
 * Plan 08-02 RP1-3: a two-state password-reset dialog.
 *
 *  State A (pre-submit): optional textarea for an explicit password (or leave
 *    blank for a server-generated 16-char temporary). [Cancel] [Reset Password]
 *  State B (post-success): success Alert with the generated password in a
 *    monospaced read-only block + Copy button. The dialog warns the admin to
 *    deliver the password via a secure side-channel and never to log it.
 *
 *  501 (sendResetLink path) — surfaces an inline destructive Alert and reverts
 *    to State A (T-08-15 mitigation: never persists or logs the plaintext).
 */

export interface ResetPasswordDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly userId: number;
  readonly userFullName: string;
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  userId,
  userFullName,
}: ResetPasswordDialogProps): JSX.Element {
  const [explicitPassword, setExplicitPassword] = useState<string>('');
  const [generated, setGenerated] = useState<ResetPasswordResponse | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const resetMutation = useMutation<
    ResetPasswordResponse,
    ApiError,
    { id: number; password: string | null }
  >({
    mutationFn: ({ id, password }) => resetPasswordRequest(id, { password, sendResetLink: false }),
    onSuccess: (resp) => {
      setGenerated(resp);
      setInlineError(null);
      // Generic toast — must NEVER include the plaintext (T-08-19).
      toast.success('Password reset successful', {
        description: 'See dialog for the one-time temporary password.',
      });
      void queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
    onError: (err) => {
      if (err.status === 501) {
        setInlineError(
          'Reset-link delivery is not configured for v1.0. Generate a temporary password instead.',
        );
      } else {
        setInlineError(err.detail ?? err.message);
      }
    },
  });

  function handleClose(nextOpen: boolean): void {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      // Clear ALL transient state on close — never leak plaintext past unmount (T-08-15).
      setExplicitPassword('');
      setGenerated(null);
      setInlineError(null);
      resetMutation.reset();
    }
  }

  function handleSubmit(): void {
    setInlineError(null);
    resetMutation.mutate({
      id: userId,
      password: explicitPassword.trim() === '' ? null : explicitPassword,
    });
  }

  async function handleCopy(): Promise<void> {
    if (generated === null) return;
    try {
      await navigator.clipboard.writeText(generated.generatedPassword);
      toast.success('Password copied');
    } catch {
      toast.error('Could not copy — please select and copy manually.');
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent data-testid="reset-password-dialog">
        {generated === null ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset password for {userFullName}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will invalidate {userFullName}&apos;s current password and force them to change
                it on next login.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-2">
              <Label htmlFor="reset-explicit-password">Optional explicit password</Label>
              <Textarea
                id="reset-explicit-password"
                rows={2}
                placeholder="Leave blank to generate a 16-char temporary password"
                value={explicitPassword}
                onChange={(e) => {
                  setExplicitPassword(e.target.value);
                }}
                disabled={resetMutation.isPending}
                data-testid="reset-password-explicit"
              />
            </div>

            {inlineError !== null && (
              <Alert variant="destructive" data-testid="reset-password-error">
                <AlertTitle>Could not reset password</AlertTitle>
                <AlertDescription>{inlineError}</AlertDescription>
              </Alert>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={resetMutation.isPending}
                data-testid="reset-password-cancel"
              >
                Cancel
              </AlertDialogCancel>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={resetMutation.isPending}
                data-testid="reset-password-confirm"
              >
                {resetMutation.isPending ? 'Resetting…' : 'Reset Password'}
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Password reset</AlertDialogTitle>
              <AlertDialogDescription>
                Copy the temporary password below and deliver it to {userFullName} via a secure
                side-channel. It will not be shown again.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <Alert variant="success" data-testid="reset-password-success-alert">
              <AlertTitle>One-time temporary password</AlertTitle>
              <AlertDescription>
                <code
                  className="font-mono text-sm break-all rounded bg-muted px-2 py-1"
                  data-testid="reset-password-generated"
                >
                  {generated.generatedPassword}
                </code>
                <p className="text-xs text-muted-foreground">
                  Communicate this via a secure channel. Do not send over plain email or chat.
                </p>
              </AlertDescription>
            </Alert>

            <AlertDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void handleCopy();
                }}
                data-testid="reset-password-copy"
              >
                Copy password
              </Button>
              <Button
                type="button"
                onClick={() => {
                  handleClose(false);
                }}
                data-testid="reset-password-done"
              >
                Done
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
